import { useEffect, useRef } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

/**
 * Achtergrondkaart: BRT-Achtergrondkaart "grijs" van PDOK (Kadaster), rustig en gratis.
 * Alleen de kaarttegels komen van PDOK; Leaflet zelf zit in de bundel (geen CDN).
 */
export const TILE_URL = "https://service.pdok.nl/brt/achtergrondkaart/wmts/v2_0/grijs/EPSG:3857/{z}/{x}/{y}.png";
export const TILE_ATTRIBUTION = 'Kaart © <a href="https://www.kadaster.nl" target="_blank" rel="noopener">Kadaster</a>';
/** Midden van Nederland, als er nog niets op de kaart staat. */
export const NL_CENTER: L.LatLngTuple = [52.15, 5.3];

export interface MapPin {
  id: string;
  lat: number;
  lng: number;
  /** Tekst in de pin (bv. de gebouwcode). */
  label: string;
  /** Toegankelijke naam (bv. "W455 Weena 455"). */
  title: string;
  tone?: "default" | "urgent";
  selected?: boolean;
}

/** De pin is zelf het markerelement (met echte maat); CSS `translate` zet de punt op de locatie. */
const pinIcon = (p: MapPin) =>
  L.divIcon({
    className: `mappin${p.tone === "urgent" ? " urgent" : ""}${p.selected ? " sel" : ""}`,
    html: escapeHtml(p.label),
    iconSize: null as unknown as L.PointExpression,
  });

const escapeHtml = (s: string) =>
  s.replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c]!);

/**
 * Leaflet-kaart met pinnen. `onPick` maakt een klik op de kaart een locatiekeuze;
 * `draggable` laat de (enige) pin verslepen.
 */
export function BaseMap({
  pins,
  onPinClick,
  onPick,
  draggable = false,
  onDrag,
  className = "",
  label,
  fitKey,
}: {
  pins: MapPin[];
  onPinClick?: (id: string) => void;
  onPick?: (lat: number, lng: number) => void;
  draggable?: boolean;
  onDrag?: (lat: number, lng: number) => void;
  className?: string;
  /** Toegankelijke naam van de kaart. */
  label: string;
  /** Verandert deze waarde, dan zoomt de kaart opnieuw op alle pinnen. */
  fitKey?: string;
}) {
  const el = useRef<HTMLDivElement>(null);
  const map = useRef<L.Map | null>(null);
  const layer = useRef<L.LayerGroup | null>(null);
  const cb = useRef({ onPinClick, onPick, onDrag });
  cb.current = { onPinClick, onPick, onDrag };

  useEffect(() => {
    const m = L.map(el.current!, { zoomControl: true, attributionControl: true }).setView(NL_CENTER, 7);
    L.tileLayer(TILE_URL, { maxZoom: 19, minZoom: 6, attribution: TILE_ATTRIBUTION }).addTo(m);
    m.attributionControl.setPrefix(false);
    m.on("click", (e: L.LeafletMouseEvent) => cb.current.onPick?.(e.latlng.lat, e.latlng.lng));
    layer.current = L.layerGroup().addTo(m);
    map.current = m;
    // Kaart in een venster of paneel: maat pas na de eerste weergave bekend.
    const ro = new ResizeObserver(() => m.invalidateSize());
    ro.observe(el.current!);
    return () => {
      ro.disconnect();
      m.remove();
      map.current = null;
    };
  }, []);

  useEffect(() => {
    const g = layer.current;
    if (!g) return;
    g.clearLayers();
    for (const p of pins) {
      const mk = L.marker([p.lat, p.lng], {
        icon: pinIcon(p),
        title: p.title,
        // Toetsenbord zelf afgehandeld (zie hieronder); focus blijft wel mogelijk.
        keyboard: false,
        draggable,
        riseOnHover: true,
        zIndexOffset: p.selected ? 1000 : 0,
      });
      mk.on("click", () => cb.current.onPinClick?.(p.id));
      mk.on("dragend", () => {
        const ll = mk.getLatLng();
        cb.current.onDrag?.(ll.lat, ll.lng);
      });
      mk.addTo(g);
      const iconEl = mk.getElement();
      if (iconEl) {
        iconEl.setAttribute("role", "button");
        iconEl.setAttribute("aria-label", p.title);
        iconEl.dataset.pin = p.id;
        iconEl.tabIndex = 0;
        // Toetsenbord: Enter of spatie kiest de pin.
        iconEl.addEventListener("keydown", (e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            cb.current.onPinClick?.(p.id);
          }
        });
      }
    }
  }, [pins, draggable]);

  // Inzoomen op de pinnen (één pin: straatniveau).
  useEffect(() => {
    const m = map.current;
    if (!m || !pins.length) return;
    if (pins.length === 1) m.setView([pins[0]!.lat, pins[0]!.lng], Math.max(m.getZoom(), 16));
    else
      m.fitBounds(L.latLngBounds(pins.map((p) => [p.lat, p.lng] as L.LatLngTuple)), { padding: [40, 40], maxZoom: 15 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fitKey]);

  return <div ref={el} className={`basemap ${className}`} role="region" aria-label={label} />;
}
